import { useEffect, useRef } from 'react';
import L from 'leaflet';
import { useT } from '@/store';
import { useOrderTruckRoute } from '@/hooks/useOrders';
import { SkeletonRows } from '@/components/ui/Panel';
import { basemapLayer } from '@/lib/basemap';

/**
 * V-298/299. Can the truck ACTUALLY assigned to this order legally reach
 * the site — karjeras -> objektas, svorio/aukščio apribojimų klausimas, ne
 * apmokestinimo (žr. OrderRouteMap.jsx's OrderRoute for the "where it
 * actually drove" GPS trail this sits above; this is the plan, that is the
 * record).
 *
 * `no_legal_route` is the reason this component exists at all: Valhalla
 * answered and there is no legal road for this vehicle's weight. That is
 * not a routing failure to apologize for — it is the single most useful
 * sentence this panel can say, and it is said as a loud alert, not a muted
 * aside, because the point is for a dispatcher to see it BEFORE dispatching
 * the driver, not after a call about a truck stuck under a bridge.
 */

// Deliberately not teal/orange/cyan: OrderRouteMap.jsx's TrailMap already
// owns cyan for the actual GPS trail on the SAME modal, and LiveMap.jsx
// owns teal for quarries and orange for the truck marker. A dispatcher
// scrolling from one map to the other must be able to tell "the plan" from
// "what happened" at a glance, not by reading a legend.
const PLANNED_ROUTE_COLOR = '#8B7FE8';
const ORIGIN_COLOR = '#22c55e'; // same green TrailMap uses for a trail's start
const DEST_COLOR = '#f43f5e';   // same rose TrailMap uses for a trail's end

function TruckRouteMap({ coordinates }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current || !coordinates.length) return undefined;
    const map = L.map(containerRef.current, { zoomControl: true, attributionControl: true });
    basemapLayer({ dark: true }).addTo(map);

    const latlngs = coordinates;
    L.polyline(latlngs, { color: PLANNED_ROUTE_COLOR, weight: 4, opacity: 0.85 }).addTo(map);
    L.circleMarker(latlngs[0], { radius: 6, color: ORIGIN_COLOR, fillOpacity: 1 }).addTo(map);
    L.circleMarker(latlngs[latlngs.length - 1], { radius: 6, color: DEST_COLOR, fillOpacity: 1 }).addTo(map);
    map.fitBounds(L.latLngBounds(latlngs), { padding: [18, 18] });

    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [coordinates]);

  return <div ref={containerRef} className="h-44 w-full rounded-[11px] border border-line" />;
}

function VehicleBadge({ vehicle, t }) {
  if (!vehicle) return null;
  const parts = [];
  if (vehicle.capacity_tons) parts.push(`${vehicle.capacity_tons} t`);
  if (vehicle.axle_count) parts.push(t('truckRoute.axlesN', { n: vehicle.axle_count }));
  if (!parts.length) return null;
  return <span>{parts.join(' · ')}</span>;
}

export function TruckRouteSection({ orderId }) {
  const t = useT();
  const { data, isLoading } = useOrderTruckRoute(orderId);

  if (isLoading) {
    return (
      <section>
        <h3 className="panel-title">{t('truckRoute.heading')}</h3>
        <SkeletonRows rows={2} />
      </section>
    );
  }
  if (!data) return null;

  if (data.state === 'no_legal_route') {
    return (
      <section>
        <h3 className="panel-title">{t('truckRoute.heading')}</h3>
        <div
          role="alert"
          className="rounded-[10px] border border-red/25 bg-red/8 px-3 py-2 text-[12px] text-red-ink"
        >
          {t('truckRoute.noLegalRoute')}
          {data.vehicle ? (
            <span className="ml-1 font-bold">
              (<VehicleBadge vehicle={data.vehicle} t={t} />)
            </span>
          ) : null}
        </div>
      </section>
    );
  }

  if (data.state === 'vehicle_unknown') {
    // Said rather than hidden — same reasoning as OrderRoute's
    // route.noneAtAll a few sections below: an absent section reads as
    // either "checked, nothing wrong" or "nobody looked", and a dispatcher
    // cannot tell those apart from silence.
    return (
      <section>
        <h3 className="panel-title">{t('truckRoute.heading')}</h3>
        <p className="text-[11.5px] leading-snug text-dim">{t('truckRoute.vehicleUnknown')}</p>
      </section>
    );
  }

  if (data.state === 'unavailable') {
    return (
      <section>
        <h3 className="panel-title">{t('truckRoute.heading')}</h3>
        <p className="text-[11.5px] leading-snug text-amber">{t('truckRoute.unavailable')}</p>
      </section>
    );
  }

  // state === 'ok'
  return (
    <section className="space-y-2">
      <h3 className="panel-title">{t('truckRoute.heading')}</h3>
      <TruckRouteMap coordinates={data.route.coordinates} />
      <p className="text-[11.5px] text-dim">
        {data.route.distance_km} km · {data.route.duration_minutes} min
        {' · '}
        <VehicleBadge vehicle={data.vehicle} t={t} />
      </p>
      <p className="text-[11px] text-dim">{t('truckRoute.explainer')}</p>
    </section>
  );
}
